import React from 'react';

import type { MessageItemProps } from '@ergeon/core-components';

import { UnifiedDialogEntry } from '../../../../types';
import UnreadDivider from '../components/UnreadDivider';
import UnreadIndicator from '../components/UnreadIndicator';

export const injectUnreadDivider = (
  messages: MessageItemProps[],
  sortedEntries: UnifiedDialogEntry[],
  lastReadAt: string | null,
  onMarkRead?: () => void,
  unreadCount?: number,
): MessageItemProps[] => {
  const createDividerAtBottom = (): MessageItemProps[] => {
    if (!unreadCount || unreadCount <= 0 || messages.length === 0) return messages;
    const dividerMessage: MessageItemProps = {
      content: <UnreadDivider onMarkRead={onMarkRead} />,
      delivered_at: new Date().toISOString(),
      alignment: 'center',
    };
    return [...messages, dividerMessage];
  };

  // No lastReadAt but API says there are unreads - show divider at bottom
  if (!lastReadAt) return createDividerAtBottom();

  // Find index of first unread entry (created_at > lastReadAt)
  const firstUnreadIndex = sortedEntries.findIndex(
    (entry) => new Date(entry.created_at) > new Date(lastReadAt),
  );

  // No unread entries found but API says there are unreads - show divider at bottom
  if (firstUnreadIndex === -1) return createDividerAtBottom();

  // Mark unread messages with indicator
  const markedMessages = messages.map((message, index) => {
    if (index >= firstUnreadIndex) {
      return {
        ...message,
        content: <UnreadIndicator>{message.content}</UnreadIndicator>,
      };
    }
    return message;
  });

  // Insert divider at the unread boundary (including at index 0 when all messages are unread)
  const dividerMessage: MessageItemProps = {
    content: <UnreadDivider onMarkRead={onMarkRead} />,
    delivered_at: lastReadAt,
    alignment: 'center',
  };

  return [...markedMessages.slice(0, firstUnreadIndex), dividerMessage, ...markedMessages.slice(firstUnreadIndex)];
};
